
import { useParams, useNavigate } from "react-router-dom";
import { incrementFunnelMetric } from "@/lib/analytics";
import { PreviewContent } from "@/components/FunnelBuilder/PreviewContent";
import { PreviewForm } from "@/components/FunnelBuilder/PreviewComponents/PreviewForm";
import { PreviewFooter } from "@/components/FunnelBuilder/PreviewComponents/PreviewFooter";
import { PreviewHeader } from "@/components/FunnelBuilder/PreviewComponents/PreviewHeader";
import { CookieConsent } from "@/components/FunnelBuilder/PreviewComponents/CookieConsent";
import { FunnelLoading, FunnelNotFound } from "@/components/FunnelBuilder/PreviewComponents/FunnelLoadingStates";
import { BlogPostPreview } from "@/components/FunnelBuilder/PreviewComponents/BlogPostPreview";
import { useFunnelData } from "@/hooks/useFunnelData";
import { useFunnelForm } from "@/hooks/useFunnelForm";
import { getThemeColors } from "@/utils/getThemeColors";
import { useEffect } from "react";

interface PublicFunnelPreviewProps {
  subdomain?: string;
}

const PublicFunnelPreview = ({ subdomain: propSubdomain }: PublicFunnelPreviewProps) => {
  const params = useParams<{ subdomain: string; slug: string; postSlug?: string }>();
  const navigate = useNavigate();
  const subdomain = propSubdomain || params.subdomain;
  const currentPathSlug = params.slug;
  const postSlug = params.postSlug;

  const { funnel, isLoading, currentSlug, setCurrentSlug } = useFunnelData(subdomain, currentPathSlug);
  const { 
    showForm, 
    setShowForm, 
    isSubmitting, 
    isSubmitted, 
    formValues, 
    handleInputChange, 
    handleFormButtonClick, 
    handleSubmit 
  } = useFunnelForm(funnel);

  // Update URL when page changes
  const handlePageChange = (slug: string) => {
    if (params.subdomain) {
      navigate(`/f/${params.subdomain}/${slug}`);
    } else if (slug) {
      navigate(`/${slug}`);
    }
    setCurrentSlug(slug);
  };

  // Handle back navigation from blog post
  const handleBackToBlogs = () => {
    const blogPage = funnel.pages.find(page => page.id === funnel.blogPageId);
    if (blogPage) {
      handlePageChange(blogPage.slug);
    }
  };

  useEffect(() => {
    if (funnel?.id) {
      try {
        incrementFunnelMetric(funnel.id, 'visitors');
      } catch (error) {
        console.error("Error updating metrics:", error);
      }
    }
  }, [funnel?.id]);

  useEffect(() => {
    if (funnel?.seoConfig) {
      document.title = funnel.seoConfig.title || funnel.siteName;
      
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', funnel.seoConfig.metaDescription || `Welcome to ${funnel.siteName}`);
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', funnel.seoConfig.metaKeywords);
      }

      const ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage) {
        ogImage.setAttribute('content', funnel.seoConfig.ogImage || funnel.logo);
      }
    }
  }, [funnel?.seoConfig, funnel?.siteName, funnel?.logo]);

  if (isLoading) return <FunnelLoading />;
  if (!funnel) return <FunnelNotFound />;

  const colors = getThemeColors(funnel);
  const currentPage = funnel.pages.find(page => page.slug === currentSlug) || 
                     funnel.pages.find(page => page.isMainPage) || 
                     funnel.pages[0];

  // Find current blog post if we're on a post route
  const currentPost = postSlug ? funnel.blogPosts?.find(post => post.slug === postSlug || post.id === postSlug) : null;

  return (
    <div 
      className="min-h-screen w-full overflow-x-hidden flex flex-col"
      style={{ 
        background: `linear-gradient(180deg, ${colors.secondary}20, ${colors.light}20)`,
      }}
    >
      <div className="flex-1 flex flex-col py-8">
        <div className="max-w-layout mx-auto w-full bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-slate-200/60 overflow-hidden">
          <PreviewHeader 
            data={funnel} 
            onPageChange={handlePageChange}
          />
          
          <main className="w-full flex-1">
            <div className="p-0">
              {currentPost ? (
                <BlogPostPreview
                  post={currentPost}
                  colors={colors}
                  onBack={handleBackToBlogs}
                />
              ) : (
                <PreviewContent
                  data={funnel}
                  colors={colors}
                  onShowForm={handleFormButtonClick}
                  isSubmitted={isSubmitted}
                  currentPage={currentPage}
                />
              )}
              
              <PreviewForm
                showForm={showForm}
                setShowForm={setShowForm}
                data={funnel}
                colors={colors}
                formValues={formValues}
                handleInputChange={handleInputChange}
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                submissionLimitReached={!funnel.isPro && (funnel.submissionCount || 0) >= 15}
              />
            </div>
          </main>

          <PreviewFooter data={funnel} colors={colors} />
        </div>
      </div>
      
      {funnel.privacyConfig?.enabled && (
        <CookieConsent
          privacyConfig={funnel.privacyConfig}
          colors={colors}
        />
      )}
    </div>
  );
};

export default PublicFunnelPreview;
